/**
 * Chat API Service
 *
 * This service handles the new conversational AI chat functionality,
 * replacing the existing single-request AI Generate system.
 * It follows the same patterns as other services in the project, extending the Api base class
 * for automatic authentication and error handling.
 */

import { Api } from "./api";
import config from "../utils/config";

export type AIModel = "claude" | "gemini" | "journey_ai";

// ==================== Chat Interfaces ====================

export interface CreateChatSessionRequest {
	first_message: string;
	action_type: "new_product" | "formulate" | "score" | "optimize";
	model?: string;
}

export interface CreateChatSessionResponse {
	session_id: string;
	session_title: string;
	message: string;
}

export interface SendMessageRequest {
	role: "user";
	content: string;
	action_type: "new_product" | "formulate" | "score" | "optimize";
	model?: "claude" | "gemini" | "journey_ai";
	metadata?: {
		serving_size?: number;
		target_nutrition?: {
			protein?: number;
			calories?: number;
			carbs?: number;
			fat?: number;
			fiber?: number;
		};
		max_cost_increase?: number;
		maintain_taste?: boolean;
		product_id?: string;
		objective?: string;
		allergen_restrictions?: string[];
		dietary_requirements?: string[];
	};
}

export interface ChatMessage {
	message_id: string;
	timestamp: string;
	role: "user" | "assistant";
	content: string;
	action_type?: string;
	metadata?: any;
	status?: "sending" | "sent" | "failed"; // For optimistic UI
	isOptimistic?: boolean; // Temporary message flag
}

export interface SendMessageResponse {
	user_message: ChatMessage;
	ai_response: ChatMessage;
	message: string;
}

export interface ChatSessionDetails {
	session_id: string;
	session_title: string;
	messages: ChatMessage[];
	total_messages: number;
	has_more: boolean;
}

export interface UserSession {
	id: string;
	session_title: string;
	message_count: number;
	updated_at: string;
	last_message: string;
}

export interface LoadUserSessionsResponse {
	sessions: UserSession[];
	total_sessions: number;
	has_more: boolean;
}

/**
 * Chat Service
 * Extends the Api base class to leverage automatic authentication and error handling
 */
class ChatService extends Api {
	/**
	 * Create a new chat session
	 *
	 * @param request - The session creation request
	 * @returns Promise resolving to a CreateChatSessionResponse
	 */
	createSession = async (request: CreateChatSessionRequest): Promise<CreateChatSessionResponse> => {
		try {
			// Validate input
			if (!request.first_message || !request.first_message.trim()) {
				throw new Error("First message is required");
			}

			if (!request.action_type) {
				throw new Error("Action type is required");
			}

			const response = await this.http.post("/api/v1/chat/sessions", request);
			return response.data;
		} catch (error) {
			console.error("Error creating chat session:", error);
			throw this.handleError(error);
		}
	}

	/**
	 * Send a message to an existing chat session
	 *
	 * @param sessionId - The session ID
	 * @param request - The message request
	 * @returns Promise resolving to a SendMessageResponse
	 */
	sendMessage = async (sessionId: string, request: SendMessageRequest): Promise<SendMessageResponse> => {
		try {
			// Validate input
			if (!sessionId) {
				throw new Error("Session ID is required");
			}

			if (!request.content || !request.content.trim()) {
				throw new Error("Message content is required");
			}

			if (!request.action_type) {
				throw new Error("Action type is required");
			}

			const response = await this.http.post(`/api/v1/chat/sessions/${sessionId}/messages`, request);
			return response.data;
		} catch (error) {
			console.error("Error sending message:", error);
			throw this.handleError(error);
		}
	}

	/**
	 * Load chat session details with message history
	 *
	 * @param sessionId - The session ID
	 * @param limit - Number of messages to load (default: 50)
	 * @param offset - Offset for pagination (default: 0)
	 * @returns Promise resolving to ChatSessionDetails
	 */
	loadSessionDetails = async (
		sessionId: string,
		limit: number = 50,
		offset: number = 0
	): Promise<ChatSessionDetails> => {
		try {
			if (!sessionId) {
				throw new Error("Session ID is required");
			}

			const response = await this.http.get(
				`/api/v1/chat/sessions/${sessionId}/details?limit=${limit}&offset=${offset}`
			);
			return response.data;
		} catch (error) {
			console.error("Error loading session details:", error);
			throw this.handleError(error);
		}
	}

	/**
	 * Load user's chat sessions
	 *
	 * @param userId - The user ID
	 * @param limit - Number of sessions to load (default: 20)
	 * @param offset - Offset for pagination (default: 0)
	 * @returns Promise resolving to LoadUserSessionsResponse
	 */
	loadUserSessions = async (
		userId: string,
		limit: number = 20,
		offset: number = 0
	): Promise<LoadUserSessionsResponse> => {
		try {
			if (!userId) {
				throw new Error("User ID is required");
			}

			const response = await this.http.get(
				`/api/v1/chat/users/${userId}/sessions?limit=${limit}&offset=${offset}`
			);

			// Handle array response format
			if (Array.isArray(response.data)) {
				return {
					sessions: response.data,
					total_sessions: response.data.length,
					has_more: false
				};
			}

			return response.data;
		} catch (error) {
			console.error("Error loading user sessions:", error);
			throw this.handleError(error);
		}
	}

	/**
	 * Delete a chat session
	 *
	 * @param sessionId - The session ID
	 * @returns Promise resolving to success message
	 */
	deleteSession = async (sessionId: string): Promise<{ message: string }> => {
		try {
			if (!sessionId) {
				throw new Error("Session ID is required");
			}

			const response = await this.http.delete(`/api/v1/chat/sessions/${sessionId}/details`);
			return response.data;
		} catch (error) {
			console.error("Error deleting chat session:", error);
			throw this.handleError(error);
		}
	}

	/**
	 * Handle API errors consistently
	 */
	public handleError(error: any): Error {
		const axiosError = error as any;
		if (axiosError.response) {
			const status = axiosError.response.status;
			const errorMessage =
				axiosError.response.data?.message ||
				axiosError.response.data?.error ||
				axiosError.response.data?.detail ||
				"An error occurred";

			// Handle specific HTTP status codes
			switch (status) {
				case 401:
					return new Error("Authentication required. Please log in again.");
				case 406:
					return new Error(`Validation error: ${errorMessage}`);
				case 503:
					return new Error("AI service is currently unavailable. Please try again later.");
				case 500:
					return new Error("Something went wrong. Please try again.");
				default:
					return new Error(errorMessage);
			}
		}

		// Handle network or other errors
		return new Error(
			error instanceof Error
				? error.message
				: "Failed to connect to chat service. Please check your connection and try again."
		);
	}
}

// Create a singleton instance of the service
const chatService = new ChatService();

// Export individual API functions for backward compatibility
export const createChatSessionAPI = async (
	request: CreateChatSessionRequest
): Promise<CreateChatSessionResponse> => {
	return chatService.createSession(request);
};

export const sendChatMessageAPI = async (
	sessionId: string,
	request: SendMessageRequest
): Promise<SendMessageResponse> => {
	return chatService.sendMessage(sessionId, request);
};

export const loadChatSessionAPI = async (
	sessionId: string,
	limit?: number,
	offset?: number
): Promise<ChatSessionDetails> => {
	return chatService.loadSessionDetails(sessionId, limit, offset);
};

export const loadUserChatSessionsAPI = async (
	userId: string,
	limit?: number,
	offset?: number
): Promise<LoadUserSessionsResponse> => {
	return chatService.loadUserSessions(userId, limit, offset);
};

export const deleteChatSessionAPI = async (
	sessionId: string
): Promise<{ message: string }> => {
	return chatService.deleteSession(sessionId);
};

export default chatService;